import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { GeneralNote, ShareGeneralNote } from "../api/types";

// Suggested tags for a general note. The server stores any short label, so this
// list can grow without a migration; "" = untagged.
export const NOTE_TAGS = ["Mastering", "EQ", "Noise", "Performance", "Technical"] as const;

/**
 * The notes as plain text for pasting into an email: one heading per tag, each
 * note a "- " paragraph under it. Suggested tags come first in picker order,
 * then any other tag alphabetically, then untagged notes under "Other".
 */
export function notesAsText(notes: ShareGeneralNote[]): string {
  const groups = new Map<string, string[]>();
  for (const n of notes) {
    const tag = n.tag.trim();
    groups.set(tag, [...(groups.get(tag) ?? []), n.text.trim()]);
  }
  const rank = (t: string) => {
    const i = (NOTE_TAGS as readonly string[]).indexOf(t);
    return t === "" ? NOTE_TAGS.length + 1 : i === -1 ? NOTE_TAGS.length : i;
  };
  return [...groups.keys()]
    .sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
    .map((tag) => {
      // A note's own line breaks are indented so they read as part of its bullet.
      const items = groups.get(tag)!.map((t) => `- ${t.replace(/\n/g, "\n  ")}`);
      return [tag || "Other", ...items].join("\n");
    })
    .join("\n\n");
}

/**
 * A project's general notes — production-wide observations that aren't a place
 * in the audio. Shared by the report's notes drawer (read + write) and the
 * owner's Pickups Package (read). Pass `live` on the Package so notes written in
 * another tab appear without a reload, like the pickups do.
 */
export function useGeneralNotes(projectId: string, live = false) {
  const qc = useQueryClient();
  const key = ["general-notes", projectId];
  const base = `/api/modules/proofing/projects/${projectId}/notes`;

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api<{ notes: GeneralNote[] }>(base).then((r) => r.notes),
    enabled: !!projectId,
    refetchOnWindowFocus: live,
    refetchInterval: live ? 8000 : false,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  const addM = useMutation({
    mutationFn: (v: { text: string; tag: string; chapter_id?: string | null }) =>
      api<GeneralNote>(base, { method: "POST", body: JSON.stringify(v) }),
    onSettled: invalidate,
  });
  const updateM = useMutation({
    mutationFn: (v: { id: string; text: string; tag: string }) =>
      api<GeneralNote>(`${base}/${v.id}`, {
        method: "PATCH",
        body: JSON.stringify({ text: v.text, tag: v.tag }),
      }),
    onSettled: invalidate,
  });
  const removeM = useMutation({
    mutationFn: (id: string) => api(`${base}/${id}`, { method: "DELETE" }),
    onMutate: (id) => qc.setQueryData<GeneralNote[]>(key, (cur) => (cur ?? []).filter((n) => n.id !== id)),
    onSettled: invalidate,
  });

  return {
    list: data ?? [],
    isLoading,
    // Add/update aren't optimistic: the drawer keeps what you typed until the
    // server has it, so a failed save never silently loses a note.
    add: addM.mutateAsync,
    adding: addM.isPending,
    update: updateM.mutateAsync,
    remove: (id: string) => removeM.mutate(id),
  };
}
